import React from "react";
import dayjs from "dayjs";
import moment from "moment";
import { Box, Chip, MenuItem, Select, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { MdRepeat } from "react-icons/md";
import {
  MAX_REPEAT_MONTHS,
  REPEAT_PRESETS,
  customRange,
  describeSettings,
  effectiveEndDate,
  floorFor,
  isWeekly,
  maxRepeatEndDate,
  sortedCustomDates,
  weekdaysFor,
} from "../../../utilities/recurrence";

const DAY_FMT = "YYYY-MM-DD";
const WEEKDAY_INITIALS = ["S", "M", "T", "W", "T", "F", "S"];
const PRIMARY = "#256FE8";

const fieldSx = {
  "& legend": { display: "none" },
  "& fieldset": { top: 0 },
  width: "100%",
  backgroundColor: "#fff",
};

const labelSx = { fontSize: 13, color: "#6A6E8A", mb: 0.75 };

/** Custom mode: a calendar day that shows as selected when it's picked. */
const PickedDay = ({ picked = [], day, ...other }) => (
  <PickersDay
    {...other}
    day={day}
    selected={picked.includes(day.format(DAY_FMT))}
  />
);

/**
 * The repeat panel on "Assign a task" and in the Edit repeat dialog.
 *
 * Props:
 *   value, onChange            — settings (see utilities/recurrence.js)
 *   startDate                  — the Assign On date (a saved series' start)
 *   onStartDateChange?(Date)   — omit to make the start read-only (editing)
 *   floorDate?                 — editing a series under way: today
 *   stacked?                   — Frequency above the days (narrow containers like a dialog)
 */
const RepeatOptions = ({
  value,
  onChange,
  startDate,
  onStartDateChange,
  floorDate,
  stacked = false,
}) => {
  const update = (patch) => onChange({ ...value, ...patch });

  const floor = floorFor(startDate, floorDate);
  const endDate = effectiveEndDate(value, floor);
  const chipDays = weekdaysFor(value);
  const { min: customMin, max: customMax } = customRange(startDate, floorDate);
  const pickedDates = sortedCustomDates(value, startDate, floorDate);
  const summary = describeSettings(value, startDate, floorDate);

  const changePreset = (preset) => {
    // Keep at least the start date's weekday when moving to a pick-your-days preset.
    const byWeekday =
      (preset === "select_days" || preset === "biweekly") && value.byWeekday.length === 0
        ? [startDate.getDay()]
        : value.byWeekday;
    update({ preset, byWeekday });
  };

  // On Weekdays/Weekends, clicking a day switches to "Select days" with that change.
  const toggleWeekday = (day) => {
    const next = chipDays.includes(day)
      ? chipDays.filter((d) => d !== day)
      : [...chipDays, day];
    update({ preset: value.preset === "biweekly" ? "biweekly" : "select_days", byWeekday: next });
  };

  const toggleCustomDate = (day) => {
    const has = value.customDates.includes(day);
    update({
      customDates: has
        ? value.customDates.filter((d) => d !== day)
        : [...value.customDates, day],
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          border: "1px solid #E5E7EB",
          borderRadius: "10px",
          backgroundColor: "#F9FAFC",
          p: { xs: 2, sm: 2.5 },
          mb: 2,
        }}
      >
        {value.preset !== "custom" && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              mb: 2,
            }}
          >
            <div>
              <Typography sx={labelSx}>Start Date</Typography>
              <DatePicker
                sx={fieldSx}
                slotProps={{ textField: { size: "small" } }}
                format="DD MMM YYYY"
                value={dayjs(startDate)}
                minDate={dayjs()}
                disabled={!onStartDateChange}
                onChange={(d) => d && d.isValid() && onStartDateChange?.(d.toDate())}
              />
            </div>
            <div>
              <Typography sx={labelSx}>End Date</Typography>
              <DatePicker
                sx={fieldSx}
                slotProps={{ textField: { size: "small" } }}
                format="DD MMM YYYY"
                value={dayjs(endDate)}
                minDate={dayjs(floor)}
                maxDate={dayjs(maxRepeatEndDate(floor))}
                onChange={(d) => d && d.isValid() && update({ endDate: d.toDate() })}
              />
            </div>
          </Box>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: isWeekly(value.preset) && !stacked ? "1fr 1fr" : "1fr",
            },
            gap: 2,
          }}
        >
          <div>
            <Typography sx={labelSx}>Frequency</Typography>
            <Select
              size="small"
              fullWidth
              sx={fieldSx}
              value={value.preset}
              onChange={(e) => changePreset(e.target.value)}
            >
              {REPEAT_PRESETS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </div>

          {isWeekly(value.preset) && (
            <div>
              <Typography sx={labelSx}>Select days</Typography>
              <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {WEEKDAY_INITIALS.map((initial, day) => {
                  const selected = chipDays.includes(day);
                  return (
                    <Box
                      key={day}
                      component="button"
                      type="button"
                      role="checkbox"
                      aria-checked={selected}
                      onClick={() => toggleWeekday(day)}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "6px",
                        border: "1px solid",
                        borderColor: selected ? PRIMARY : "#E5E7EB",
                        backgroundColor: selected ? "#EFF6FF" : "#fff",
                        color: selected ? PRIMARY : "#282C34",
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: "pointer",
                        p: 0,
                        "&:hover": { borderColor: PRIMARY },
                      }}
                    >
                      {initial}
                    </Box>
                  );
                })}
              </Box>
            </div>
          )}
        </Box>

        {value.preset === "custom" && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={labelSx}>Click the dates it should repeat on</Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "flex-start",
              }}
            >
              <Box
                sx={{
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  maxWidth: "100%",
                  overflowX: "auto",
                }}
              >
                <DateCalendar
                  value={null}
                  referenceDate={dayjs(pickedDates[0] || customMin.format(DAY_FMT))}
                  minDate={dayjs(customMin.format(DAY_FMT))}
                  maxDate={dayjs(customMax.format(DAY_FMT))}
                  onChange={(d) => d && toggleCustomDate(d.format(DAY_FMT))}
                  slots={{ day: PickedDay }}
                  slotProps={{ day: { picked: pickedDates } }}
                />
              </Box>
              <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, color: "#6A6E8A", mb: 1 }}>
                  {pickedDates.length === 0
                    ? "No dates picked yet"
                    : pickedDates.length === 1
                      ? "1 date selected"
                      : `${pickedDates.length} dates selected`}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {pickedDates.map((d) => (
                    <Chip
                      key={d}
                      size="small"
                      label={moment(d, DAY_FMT).format("D MMM")}
                      onDelete={() => toggleCustomDate(d)}
                      sx={{
                        backgroundColor: "#EFF6FF",
                        color: PRIMARY,
                        border: `1px solid ${PRIMARY}`,
                        "& .MuiChip-deleteIcon": { color: PRIMARY },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        <Typography sx={{ fontSize: 12, color: "#6A6E8A", mt: 1.5 }}>
          Repeats can run for up to {MAX_REPEAT_MONTHS} months
        </Typography>

        {summary && (
          <Box
            sx={{
              mt: 1.5,
              p: 1.5,
              borderRadius: "8px",
              backgroundColor: "#EFF6FF",
              color: "#1E40AF",
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            <MdRepeat size={16} color="#1E40AF" style={{ flexShrink: 0 }} />
            <span>{summary}</span>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default RepeatOptions;
